import {
  AssignmentsCompleteSVG,
  Button,
  InfoModal,
  useModal,
} from "@newsela/angelou";
import PropTypes from "prop-types";

import { withTrackEvent } from "@newsela/snitch";
import Skeleton from "@/components/Skeleton";
import { GRADING_QUERY_PARAMS } from "@/grading/constants";
import NoAssignedWorkError from "@/grading/GradingPageError/NoAssignedWorkError";
import { MONOLITH_ORIGIN } from "@/monolithIntegration/monolithIntegration.constants";
import formatDate from "@/utils/formatDate";
import AssignmentAnchorItem from "./AssignmentAnchorItem";
import {
  activityPickerGradingButtonCss,
  assigneeLabelCss,
  gradingModalContainerCss,
  headingCss,
  iconCss,
  modalCss,
  modalHeadingCss,
} from "./ActivityPickerModal.style";

const propTypes = {
  assignments: PropTypes.array,
  assignmentsLoading: PropTypes.bool,
  contentId: PropTypes.string,
  isElaReadingSkillsStyle: PropTypes.bool,
  levelId: PropTypes.string,
  legacyArticleLevelHeaderId: PropTypes.number,
};

const ButtonWithTrackEvent = withTrackEvent(Button);

const ActivityPickerModal = ({
  assignments = [],
  assignmentsLoading,
  contentId,
  legacyArticleLevelHeaderId,
  levelId,
  isElaReadingSkillsStyle,
}) => {
  const [modalProps, setActive] = useModal(false);
  const gradingPageUrl = `/grading/${contentId}?${GRADING_QUERY_PARAMS.lexileLevel.param}=${levelId}&${GRADING_QUERY_PARAMS.headerId.param}=${legacyArticleLevelHeaderId}`;

  const renderIndependentStudentWork = () => {
    return (
      <AssignmentAnchorItem
        href={gradingPageUrl} // re-directs to grading page URL
        title="Independent student work"
        byline={"This work isn't part of an assignment"}
      />
    );
  };

  const renderAssignmentStudentWork = () => {
    if (assignmentsLoading) return <Skeleton height={80} />;
    if (assignments.length === 0) return <NoAssignedWorkError />;

    return assignments.map((assignment) => {
      const assigneeNames = assignment.assignment_groups[0]?.name;
      const assignmentClassroomName =
        assignment.assignment_classrooms[0]?.classroom.name;
      return (
        <AssignmentAnchorItem
          key={assignment.content_id}
          href={`${MONOLITH_ORIGIN}/assignment/${assignment.content_id}/`}
          title={assignment.title}
          date={`Assigned on ${formatDate(new Date(assignment.date_assigned), "mm/dd/yyyy")}`}
          assignee={
            <>
              <span css={assigneeLabelCss}> Assignees:</span>
              {assigneeNames || assignmentClassroomName}
            </>
          }
        />
      );
    });
  };

  return (
    <div css={gradingModalContainerCss}>
      <ButtonWithTrackEvent
        __cssFor={{
          root: activityPickerGradingButtonCss,
          Icon: { root: iconCss },
        }}
        dataProps={{ "data-qa-selector": "review-student-work-modal" }}
        flavor={Button.flavors.muted}
        isFullWidth
        onClick={() => setActive(true)}
        actionName="Navigate"
        streamProperties={{
          description: "Open review student work modal",
          content_id: contentId,
        }}
        {...(isElaReadingSkillsStyle && {
          icon: {
            SvgComponent: AssignmentsCompleteSVG,
            alignment: Button.iconAlignments.left,
          },
        })}
      >
        Review Student Work
      </ButtonWithTrackEvent>
      <InfoModal
        __cssFor={modalCss}
        title="Review Student Work"
        labelId="reviewStudentwork"
        onBackgroundClick={() => setActive(false)}
        {...modalProps}
      >
        <h2 css={modalHeadingCss}>Select the work you'd like to review </h2>
        <h3 css={headingCss}> Independent Work </h3>
        {renderIndependentStudentWork()}
        <h3 css={headingCss}> Assignments </h3>
        {renderAssignmentStudentWork()}
      </InfoModal>
    </div>
  );
};

ActivityPickerModal.propTypes = propTypes;

export default ActivityPickerModal;
